import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    key: "age_range",
    title: "What's your age range?",
    options: [
      { v: "under_16", l: "Under 16" },
      { v: "16-17", l: "16–17" },
      { v: "18-22", l: "18–22" },
      { v: "23-26", l: "23–26" },
      { v: "27-30", l: "27–30" },
    ],
  },
  {
    key: "location",
    title: "Where in Australia are you?",
    options: ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"].map((s) => ({ v: s, l: s })),
  },
  {
    key: "study_work",
    title: "What's your current situation?",
    options: [
      { v: "high_school", l: "Year 11–12" },
      { v: "uni_student", l: "Uni / TAFE student" },
      { v: "working", l: "Working full-time" },
      { v: "casual", l: "Casual / part-time" },
      { v: "between", l: "Between things" },
    ],
  },
  {
    key: "living_situation",
    title: "Where are you living right now?",
    options: [
      { v: "with_parents", l: "With family" },
      { v: "share_house", l: "Share house" },
      { v: "renting_alone", l: "Renting solo / partner" },
      { v: "boarding", l: "Boarding / dorms" },
    ],
  },
  {
    key: "goals",
    title: "What are you trying to do? (pick any)",
    multi: true,
    options: [
      { v: "save_money", l: "Save money" },
      { v: "first_job", l: "Get my first job" },
      { v: "move_out", l: "Move out" },
      { v: "uni", l: "Apply for uni/TAFE" },
      { v: "tax", l: "Sort tax & super" },
      { v: "mental_health", l: "Mental health" },
      { v: "drivers_licence", l: "Driver licence" },
    ],
  },
  {
    key: "focus_areas",
    title: "Which areas need most help? (pick any)",
    multi: true,
    options: [
      { v: "money-banking", l: "Money & Banking" },
      { v: "work-career", l: "Work & Career" },
      { v: "study-uni", l: "Study & Uni" },
      { v: "home-living", l: "Home & Living" },
      { v: "health-wellbeing", l: "Health" },
      { v: "legal-admin", l: "Legal & Admin" },
      { v: "relationships", l: "Relationships" },
      { v: "digital-life", l: "Digital Life" },
    ],
  },
  {
    key: "confidence",
    title: "How confident do you feel about adulting?",
    options: [
      { v: 2, l: "Lost" }, { v: 4, l: "Shaky" }, { v: 6, l: "OK" }, { v: 8, l: "Pretty good" }, { v: 10, l: "Got it" },
    ],
  },
];

export default function Onboarding() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ goals: [], focus_areas: [] });
  const [busy, setBusy] = useState(false);

  const cur = STEPS[step];

  const select = (v) => {
    if (cur.multi) {
      const arr = data[cur.key] || [];
      const next = arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
      setData({ ...data, [cur.key]: next });
    } else {
      setData({ ...data, [cur.key]: v });
    }
  };

  const isSelected = (v) => {
    const cv = data[cur.key];
    return cur.multi ? (cv || []).includes(v) : cv === v;
  };

  const canNext = cur.multi ? (data[cur.key] || []).length > 0 : data[cur.key] !== undefined;

  const next = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        age_range: data.age_range || "18-22",
        location: data.location || "NSW",
        study_work: data.study_work || "uni_student",
        living_situation: data.living_situation || "with_parents",
        goals: data.goals || [],
        confidence: typeof data.confidence === "number" ? data.confidence : 5,
        focus_areas: data.focus_areas || [],
      };
      const { data: u } = await api.post("/onboarding", payload);
      setUser(u);
      toast.success("Roadmap ready!");
      nav("/dashboard");
    } catch {
      toast.error("Couldn't save, try again?");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-aurora flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* progress */}
        <div className="flex gap-1.5 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-neonpink shadow-glow" : "bg-white/10"}`}/>
          ))}
        </div>
        <div className="glass-card-strong p-8 md:p-10" data-testid="onboarding-step">
          <div className="text-xs font-bold tracking-widest text-neoncyan mb-2">STEP {step + 1} / {STEPS.length}</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">{cur.title}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
            {cur.options.map((opt) => (
              <button
                key={String(opt.v)}
                onClick={() => select(opt.v)}
                data-testid={`onboarding-option-${cur.key}-${opt.v}`}
                className={`text-left px-5 py-4 rounded-2xl border transition font-semibold ${
                  isSelected(opt.v)
                    ? "bg-neonpink/15 border-neonpink text-white shadow-glow"
                    : "bg-white/4 border-white/10 hover:border-white/30 text-white/85"
                }`}
              >{opt.l}</button>
            ))}
          </div>

          <div className="flex justify-between mt-10">
            <button
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="onboarding-back"
              className="btn-secondary-neon disabled:opacity-30 flex items-center gap-2"
            ><ArrowLeft size={16}/> Back</button>
            <button
              onClick={next}
              disabled={!canNext || busy}
              data-testid="onboarding-next"
              className="btn-primary-neon disabled:opacity-50 flex items-center gap-2"
            >
              {step === STEPS.length - 1 ? (busy ? "Building roadmap..." : "Build my roadmap") : "Next"} <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
